import { message } from 'antd'

import { soyshareApi } from '../../../../../services/api'
import { getMessage } from '../../../../../util/IntlMessages'
import { isMemberLevel } from '../../../../../util/profiles'

const api = soyshareApi.injectEndpoints({
  endpoints: (build) => ({
    getActionTypes: build.query({
      query: ({ ...params } = {}) => ({
        url: '/action-types/dropdown/list',
        params: { todos: false, active: true, ...params }
      })
    }),

    getActionStatus: build.query({
      query: ({ ...params } = {}) => ({
        url: '/action-status/dropdown/list',
        params: { todos: false, active: true, ...params }
      })
    }),

    getCustomers: build.query({
      query: ({ actionTypeId, ...params } = {}) => ({
        url: `/customers/getcustomersbyactiontype/${actionTypeId}`,
        params
      })
    }),

    getMembers: build.query({
      query: ({ customerId = -1 } = {}) => ({ url: `/members/getmembersbycustomer/${customerId}` }),
      transformResponse: (response, _, { currentUser }) => {
        const isMember = isMemberLevel()
        return isMember ? response.filter((member) => Number(member.value) === Number(currentUser.memberId)) : response
      }
    }),

    getLocations: build.query({
      query: ({ customerId = -1, memberId = -1, currentUser } = {}) => {
        const isMember = isMemberLevel()
        memberId = isMember ? currentUser?.memberId || -1 : memberId
        return { url: `/locations/getlocationsbycustomer/${customerId}/member/${memberId}` }
      }
    }),

    getHarvests: build.query({
      query: ({ ...params } = {}) => ({
        url: '/safra/dropdown',
        params: { todos: false, active: true, withcalendar: true, ...params }
      })
    }),

    getCustomFields: build.query({
      query: ({ actionTypeId }) => ({ url: `/action-custom-fields/action-type/${actionTypeId}` }),
      transformResponse: (response = []) => {
        const fields = new Map()

        const orderedFields = [...response].sort((a, b) => {
          const rowA = Number(a?.fieldRow ?? Number.MAX_SAFE_INTEGER)
          const rowB = Number(b?.fieldRow ?? Number.MAX_SAFE_INTEGER)

          if (rowA !== rowB) {
            return rowA - rowB
          }

          const columnA = Number(a?.fieldColumn ?? Number.MAX_SAFE_INTEGER)
          const columnB = Number(b?.fieldColumn ?? Number.MAX_SAFE_INTEGER)

          return columnA - columnB
        })

        orderedFields.forEach((field) => {
          const mapKey = field?.fieldGroup ?? field?.fieldName
          const isFieldInGroup = !!field?.fieldGroup
          const groupType = field?.groupType ?? null

          if (isFieldInGroup) {
            const existingGroup = fields.get(mapKey)

            const resolvedGroupType = existingGroup?.groupType ?? groupType

            const group = existingGroup || {
              groupType: resolvedGroupType,
              isGroup: true,
              isGroupUnderGroup: !!resolvedGroupType,
              fields: []
            }

            group.groupType = resolvedGroupType
            group.isGroupUnderGroup = !!resolvedGroupType
            group.fields.push(field)
            fields.set(mapKey, group)
            return
          }

          fields.set(mapKey, {
            isGroup: false,
            isGroupUnderGroup: false,
            groupType: null,
            fields: [field]
          })
        })

        return fields
      }
    }),

    getFieldOptionRelationships: build.query({
      query: ({ mainOptionId }) => ({
        url: `/form-fields/relationships/select-options/option/${mainOptionId}`
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        message.loading({ content: getMessage('fillingRelationships'), key: 'fillingRelationships' })
        await queryFulfilled
        message.destroy('fillingRelationships')
        message.success({
          content: getMessage('successSetRelationships'),
          key: 'setRelationships',
          duration: 1.5
        })
      }
    })
  })
})

export const {
  useGetActionTypesQuery,
  useGetActionStatusQuery,
  useGetCustomersQuery,
  useGetMembersQuery,
  useGetLocationsQuery,
  useGetHarvestsQuery,
  useGetCustomFieldsQuery,
  useGetFieldOptionRelationshipsQuery
} = api
